import moment from "moment";

import { PredefinedAppointment } from "@/Appointments/Api/Dto/Responses/PredefinedAppointment";
import { PredefinedRuleSet, PredefinedScheduleRuleSetSlotDTO } from "@/Appointments/Api/Dto/Responses/PredefinedRuleSet";
import { TemplateInfo } from "@/Appointments/Models/Scheduling/TemplateInfo";
import { Appointment } from "@/Common/Api/Appointment/Dto/Appointment";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { CalendarSlotType } from "@/Common/Models/UI/Enums";
import { PredefinedAppointmentGroup } from "./PredefinedAppointmentGroup";

class WorkDayV2 {
    public date: Date;
    public EmployeeId: string;
    public HasRuleSet: boolean;

    public calendarSlots: CalendarSlotV2[];
}

class CalendarSlotV2 {
    public id;

    public from: Date;
    public to: Date; // Adjusted for MinHeight considering the Resolution
    public DisplayedDurationInMinutesComputed: number;

    public Type: CalendarSlotType;
    public minorAxisResolutionInMinutes: number;

    // Dynamic
    public IsPlaced: boolean = false;
    public MaxCollisionsInCollisionRange: number;
    public HorizontalPositionIndexZeroBase: number;
    // ----

    // fixed
    public minorAxisPositionIndexZeroBase: number;
    public minorAxisPositionIndexFractionPourcent: number;
    // ----

    public Model: any;
    public fromRaw: Date;
    public toRAW: Date;

    public constructor(init?: Partial<CalendarSlotV2>) {
        Object.assign(this, init);
    }

    public static FromAppoinment(appointment: Appointment): CalendarSlotV2 {
        return new CalendarSlotV2({
            id: GuidHelper.NewGuid(),
            fromRaw: moment(appointment.StartTime).toDate(),
            toRAW: moment(appointment.EndTime).toDate(),
            Model: appointment,
            Type: CalendarSlotType.Appointment
        });
    }

    public static FromPredefinedAppoinment(predefinedAppointment: PredefinedAppointment): CalendarSlotV2 {
        const startDate = moment(predefinedAppointment.Start).toDate();
        return new CalendarSlotV2({
            id: GuidHelper.NewGuid(),
            fromRaw: startDate,
            toRAW: startDate.addTicks(predefinedAppointment.Duration),
            Model: predefinedAppointment,
            Type: CalendarSlotType.PredefinedAppointment
        });
    }

    public static FromPredefinedRuleSet(ruleSet: PredefinedRuleSet): CalendarSlotV2 {
        return new CalendarSlotV2({
            id: GuidHelper.NewGuid(),
            fromRaw: moment(ruleSet.StartDate).toDate(),
            toRAW: moment(ruleSet.EndDate).toDate(),
            Model: ruleSet,
            Type: CalendarSlotType.RuleSet
        });
    }

    public static FromPredefinedRuleSetSlot(ruleSetSlot: PredefinedScheduleRuleSetSlotDTO): CalendarSlotV2 {
        return new CalendarSlotV2({
            id: GuidHelper.NewGuid(),
            fromRaw: moment(ruleSetSlot.StartDate).toDate(),
            toRAW: moment(ruleSetSlot.EndDate).toDate(),
            Model: ruleSetSlot,
            Type: CalendarSlotType.RuleSetSlot
        });
    }

    public static fromTemplateInfo(templateInfo: TemplateInfo): CalendarSlotV2 {
        const startDate = moment(templateInfo.StartDate)
            .setToday()
            .toDate();

        return new CalendarSlotV2({
            id: GuidHelper.NewGuid(),
            fromRaw: startDate,
            toRAW: startDate.addTicks(templateInfo.Duration),
            Model: templateInfo,
            Type: CalendarSlotType.PredefinedAppointment
        });
    }

    public static fromPredefinedAppointmentGroup(predefinedGroup: PredefinedAppointmentGroup): CalendarSlotV2 {
        return new CalendarSlotV2({
            id: GuidHelper.NewGuid(),
            fromRaw: moment(predefinedGroup.MaxStart).toDate(),
            toRAW: moment(predefinedGroup.MaxEnd).toDate(),
            Model: predefinedGroup,
            Type: CalendarSlotType.PredefinedGroup
        });
    }

    public GetModelA(): Appointment {
        return (this.Model as Appointment);
    }

    public GetModelP(): PredefinedAppointment {
        return (this.Model as PredefinedAppointment);
    }

    public GetColorClass(): string | undefined {
        if (this.Type == CalendarSlotType.PredefinedAppointment)
            return "PREDEFINED";
        else if (this.Type == CalendarSlotType.Appointment) {
            const colorClass = this.GetModelA().AppointmentStateLookupCode.toUpperCase();
            return colorClass === "MESSAGE LEFT" ? "MESSAGELEFT" : colorClass;
        }
        else if (this.Type == CalendarSlotType.RuleSet)
            return "PREDEFINEDRULESET";
        else if (this.Type == CalendarSlotType.RuleSetSlot)
            return "PREDEFINEDRULESET";
        else if (this.Type == CalendarSlotType.PredefinedGroup)
            return "PREDEFINEDGROUP";
    }

    public Init(minorAxisResolutionInMinutes: number = 5, NumberOfSubDivisionPerMajor: number): void {
        this.minorAxisResolutionInMinutes = minorAxisResolutionInMinutes;

        var MinutesPerSubDiv = (60 / NumberOfSubDivisionPerMajor);
        this.minorAxisPositionIndexZeroBase = Math.floor(this.fromRaw.getMinutes() / MinutesPerSubDiv);
        this.minorAxisPositionIndexFractionPourcent = Math.round(100 * (this.fromRaw.getMinutes() % MinutesPerSubDiv) / MinutesPerSubDiv);

        this.from = this.fromRaw;

        this.AdjustEndTimeForMinHeight();
        this.refreshSlotDisplayedDurationInMinutes();
    }

    public AdjustEndTimeForMinHeight(): void {
        var MinDurationDueToResolutionInMinutes = Math.floor(this.minorAxisResolutionInMinutes * 0.75);

        var rawDuration = Math.floor(((+this.toRAW - +this.fromRaw) / 1000) / 60);
        if (rawDuration < MinDurationDueToResolutionInMinutes)
            this.to = this.from.toMoment().add(MinDurationDueToResolutionInMinutes, "minutes").toDate();
        else
            this.to = this.toRAW;
    }

    public refreshSlotDisplayedDurationInMinutes(): void {
        var ms = +this.to - +this.fromRaw;
        this.DisplayedDurationInMinutesComputed = Math.floor((ms / 1000) / 60);
    }
}

export { CalendarSlotV2, WorkDayV2 };
